import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, ArrowUpRight } from "lucide-react";
import api, { inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Reveal, Overline } from "../components/Reveal";

export function WalletCard() {
  const [wallet, setWallet] = useState(null);
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/wallet").then((r) => setWallet(r.data)).catch(() => setWallet(false));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  if (!wallet) return null;
  const stats = [
    { label: "Current Balance", value: wallet.balance, gold: true },
    { label: "Pending", value: wallet.pending },
    { label: "Earned", value: wallet.earned },
    { label: "Redeemed", value: wallet.redeemed },
    { label: "Expired", value: wallet.expired },
  ];
  return (
    <div data-testid="cloth-cash-wallet">
      <div className="mb-8 flex flex-wrap items-center gap-4 border border-gold/50 bg-gold/5 p-6">
        <p className="text-sm text-ink/70 flex-1 min-w-48">Use your Cloth Cash on eligible purchases at both stores.</p>
        <Link to="/shop" data-testid="use-jigel-btn" className="bg-ink text-ivory px-6 py-3 text-[10px] tracking-[0.2em] uppercase hover:bg-wine transition-colors">Use at JIGEL</Link>
        <a href={settings?.avaginja_url || "https://avaginja.com"} target="_blank" rel="noreferrer" data-testid="use-avaginja-btn"
          className="border border-gold text-gold px-6 py-3 text-[10px] tracking-[0.2em] uppercase hover:bg-gold hover:text-ink transition-colors flex items-center gap-2">
          Use at AVAGINJA <ArrowUpRight size={12} strokeWidth={1.5} />
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border border border-border">
        {stats.map((s) => (
          <div key={s.label} className="bg-ivory p-6">
            <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">{s.label}</p>
            <p className={`mt-2 font-mono text-2xl ${s.gold ? "text-gold" : ""}`} data-testid={`wallet-${s.label.toLowerCase().replace(/ /g, "-")}`}>{inr(s.value)}</p>
          </div>
        ))}
      </div>
      <div className="mt-10">
        <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-4">Transactions</p>
        {wallet.transactions.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="wallet-empty">No Cloth Cash activity yet. Trade in a garment to earn your first balance.</p>
        ) : (
          <div className="border-t border-border" data-testid="wallet-transactions">
            {wallet.transactions.map((t, i) => (
              <div key={i} className="grid grid-cols-2 md:grid-cols-5 gap-2 py-4 border-b border-border text-sm">
                <span className="font-mono text-xs text-muted-foreground">{new Date(t.date).toLocaleDateString("en-IN")}</span>
                <span className="font-mono text-xs">{t.trade_in_id || t.order_id}</span>
                <span className={`font-mono ${t.amount > 0 ? "text-gold" : "text-wine"}`}>{t.amount > 0 ? "+" : ""}{inr(t.amount)}</span>
                <span className="text-ink/60 col-span-2 md:col-span-1">{t.description}</span>
                <span className="font-mono text-xs text-muted-foreground">Bal {inr(t.balance)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ClothCash() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="cloth-cash-page">
      <section className="bg-ink text-ivory">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-24 md:py-32">
          <Reveal>
            <Overline light>The Jigel Reward</Overline>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl tracking-tighter mt-4">
              Cloth <span className="italic font-light text-gold">Cash.</span>
            </h1>
            <p className="mt-8 max-w-xl text-ivory/60 text-base lg:text-lg leading-relaxed">
              When your Trade-In is reviewed and accepted, you receive Cloth Cash — store value you can spend on anything at Jigel. Cloth Cash is not cash payout; it's value toward future purchases.
            </p>
            <div className="mt-8 inline-block border border-gold/40 px-6 py-4" data-testid="cloth-cash-rule">
              <p className="text-[10px] tracking-[0.3em] uppercase text-gold">The Rule</p>
              <p className="mt-2 font-serif text-xl text-ivory/90">Cloth Cash = up to {settings?.cloth_cash_percent ?? 15}% of the approved Trade-In value.</p>
              <p className="mt-1 text-xs text-ivory/50">Calculated from the final approved value after review — never from the original MRP or purchase price.</p>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="mt-10 flex flex-wrap gap-4 justify-center lg:justify-start">
            <Link to="/shop" data-testid="shop-jigel-btn"
              className="bg-gold text-ink px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-ivory transition-colors">Shop JIGEL</Link>
            <a href={settings?.avaginja_url || "https://avaginja.com"} target="_blank" rel="noreferrer" data-testid="shop-avaginja-btn"
              className="border border-ivory/40 text-ivory px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-ivory hover:text-ink transition-colors flex items-center gap-2">
              Shop AVAGINJA <ArrowUpRight size={14} strokeWidth={1.5} />
            </a>
          </Reveal>
          {settings && (
            <Reveal delay={0.2} className="mt-8 max-w-xl mx-auto lg:mx-0">
              <p className="text-[11px] tracking-[0.15em] uppercase text-ivory/40 leading-relaxed">
                Cloth Cash is store credit, not cash: up to {settings.redemption_rules.max_percent}% of an eligible order, minimum order ₹{settings.redemption_rules.min_order}, maximum ₹{settings.redemption_rules.max_per_order} per order. No withdrawal or transfer.
              </p>
            </Reveal>
          )}
          <Reveal delay={0.15} className="mt-12 grid sm:grid-cols-3 gap-px bg-ivory/10 border border-ivory/10 text-left">
            {[["Earn", "Accepted Trade-Ins convert to Cloth Cash after final inspection."], ["Hold", "Track balance, pending offers, earned and redeemed in one ledger."], ["Spend", "Apply Cloth Cash at checkout toward new or pre-loved pieces."]].map(([t, d]) => (
              <div key={t} className="bg-ink p-8">
                <Coins size={18} strokeWidth={1} className="text-gold" />
                <h3 className="font-serif text-2xl mt-4">{t}</h3>
                <p className="mt-2 text-sm text-ivory/50 leading-relaxed">{d}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>
      <section className="max-w-[1600px] mx-auto px-5 md:px-10 py-20 md:py-28">
        {user ? (
          <>
            <Reveal><Overline>My Cloth Cash</Overline>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3 mb-10">Your wallet.</h2>
            </Reveal>
            <WalletCard />
          </>
        ) : (
          <Reveal className="text-center py-10">
            <p className="font-serif text-3xl">Sign in to view your Cloth Cash wallet.</p>
            <Link to="/auth?next=/cloth-cash" data-testid="clothcash-signin-btn"
              className="inline-block mt-8 bg-ink text-ivory px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-wine transition-colors">
              Sign In
            </Link>
          </Reveal>
        )}
      </section>
    </div>
  );
}
